// IAP 단일 래퍼 — 화면은 이 파일 밖에서 cdv-purchase API를 직접 호출하지 않는다.
// 결제 순서는 precheck → store.order → 서버 검증 → finish로 고정한다.

import { useSyncExternalStore } from "react";
import { Capacitor } from "@capacitor/core";
import { store, ProductType, Platform, LogLevel, ErrorCode } from "capacitor-plugin-cdv-purchase";
import { apiFetch, ApiError } from "./api";
import { queryClient } from "./queryClient";
import { showSuccess } from "./toast";
import { E2E_MOCK } from "./e2eMock";

export type IapPlatform = "ios" | "android";
export type BillingCycle = "monthly" | "yearly";

// 🔴 `TransactionState`는 이 패키지가 루트에서 export하지 않는다 —
//    `CdvPurchase` 전역 네임스페이스 안의 enum이라 `import { TransactionState }`는 undefined가 되고,
//    `TransactionState.APPROVED` 접근 순간 TypeError로 결제 직후 finish 단계가 통째로 죽는다.
//    enum 값 자체는 문자열로 고정돼 있으므로(www/store.d.ts:1794 `APPROVED = "approved"`)
//    리터럴 상수로 받는 편이 전역 로드 타이밍에도 안 걸린다.
const APPROVED_STATE = "approved";

export interface IapCreditProduct {
  key: string;
  productId: string;
  cb: number;
  krw: number;
  desc: string;
  bonus: number;
}

export interface IapSubscriptionProduct {
  tier: "coffee" | "gukbap";
  cycle: BillingCycle;
  productId: string;
  basePlanId: string | null;
  krw: number;
  name: string;
}

export interface IapCatalogResponse {
  ok: true;
  enabled: boolean;
  products: IapCreditProduct[];
  subscriptions: IapSubscriptionProduct[];
  first_topup_bonus: number;
  allowed?: true;
}

export interface IapSnapshot extends IapCatalogResponse {
  ready: boolean;
  loading: boolean;
  error: string | null;
  localizedPrices: Record<string, string>;
}

type StoreOffer = {
  id?: string;
  pricingPhases?: Array<{
    price?: string;
    priceMicros?: number;
    currency?: string;
    billingPeriod?: string;
    recurrenceMode?: string;
  }>;
  raw?: Record<string, unknown>;
};

type StoreProduct = {
  id: string;
  platform?: string;
  offers?: StoreOffer[];
  pricing?: { price?: string; priceMicros?: number; currency?: string };
};

type StoreTransaction = {
  platform?: string;
  state?: string;
  transactionId?: string;
  purchaseToken?: string;
  nativePurchase?: { purchaseToken?: string };
  raw?: { purchaseToken?: string };
  products?: Array<{ id?: string }>;
  finish: () => Promise<unknown> | unknown;
};

export type IapEvent =
  | { type: "processing"; productId: string }
  | { type: "success"; productId: string; response: Record<string, unknown> }
  // pending = 결제 자체는 스토어에서 끝났는데 서버 검증만 못 끝낸 상태.
  //   트랜잭션을 finish하지 않고 보존하므로 다음 앱 시작에 자동 재시도되고, 지급은 그대로 진행된다.
  //   화면은 "실패"가 아니라 "확인 중"으로 안내해야 한다(결제 화면 시안 E).
  | { type: "error"; productId: string | null; message: string; pending?: boolean };

const EMPTY: IapSnapshot = {
  ok: true,
  enabled: false,
  products: [],
  subscriptions: [],
  first_topup_bonus: 0,
  ready: false,
  loading: false,
  error: null,
  localizedPrices: {},
};

let snapshot: IapSnapshot = EMPTY;
let initializePromise: Promise<IapSnapshot> | null = null;
let listenersAttached = false;
const snapshotListeners = new Set<() => void>();
const eventListeners = new Set<(event: IapEvent) => void>();
const processingTransactions = new Set<string>();
const warnedPrices = new Set<string>();

function publish(patch: Partial<IapSnapshot>) {
  snapshot = { ...snapshot, ...patch };
  snapshotListeners.forEach((listener) => listener());
}

function emit(event: IapEvent) {
  eventListeners.forEach((listener) => listener(event));
}

export function subscribeIap(listener: () => void) {
  snapshotListeners.add(listener);
  return () => {
    snapshotListeners.delete(listener);
  };
}

export function onIapEvent(listener: (event: IapEvent) => void) {
  eventListeners.add(listener);
  return () => {
    eventListeners.delete(listener);
  };
}

export function getIapSnapshot() {
  return snapshot;
}

export function useIap() {
  return useSyncExternalStore(subscribeIap, getIapSnapshot, getIapSnapshot);
}

export function currentIapPlatform(value = Capacitor.getPlatform()): IapPlatform | null {
  if (value === "ios" || value === "android") return value;
  return null;
}

/**
 * 현재 플랫폼의 스토어 명칭. 화면 카피에 "App Store·Play 스토어"처럼 양쪽을 나란히 박으면
 * Apple 2.3.10(타 플랫폼 언급)으로 반려된다(2026-09-02 실제 반려). 반드시 이 헬퍼로 분기한다.
 */
export function storeName(platform = currentIapPlatform()): string {
  if (platform === "android") return "Play 스토어";
  if (platform === "ios") return "App Store";
  return "스토어"; // 웹 프리뷰 등 스토어가 없는 환경
}

export function shouldProcessPending(
  state: string | undefined,
  approvedState = "approved",
  alreadyProcessing = false,
) {
  return state === approvedState && !alreadyProcessing;
}

export function selectOfferForCycle(
  offers: StoreOffer[] | undefined,
  cycle?: BillingCycle,
  basePlanId?: string | null,
) {
  if (!offers?.length) return null;
  if (!cycle && !basePlanId) return offers[0];
  const period = cycle === "yearly" ? "P1Y" : "P1M";
  return (
    offers.find((offer) => {
      const rawBasePlan = String(offer.raw?.basePlanId ?? offer.raw?.base_plan_id ?? "");
      const offerId = String(offer.id ?? "");
      return !!basePlanId && (rawBasePlan === basePlanId || offerId === basePlanId || offerId.includes(basePlanId));
    }) ??
    offers.find((offer) => offer.pricingPhases?.some((phase) => phase.billingPeriod === period)) ??
    null
  );
}

export function transactionVerificationBody(transaction: StoreTransaction, platform: IapPlatform) {
  if (platform === "ios") {
    const transactionId = String(transaction.transactionId ?? "").trim();
    return transactionId ? { platform, transactionId } : null;
  }
  // purchaseType 같은 환경 신호는 가공하지 않는다. 서버가 Play 원본 응답에서 직접 판정한다.
  const purchaseToken = String(
    transaction.purchaseToken ?? transaction.nativePurchase?.purchaseToken ?? transaction.raw?.purchaseToken ?? "",
  ).trim();
  const productId = String(transaction.products?.[0]?.id ?? "").trim();
  return purchaseToken && productId ? { platform, productId, purchaseToken } : null;
}

function platformValue(platform: IapPlatform) {
  return platform === "ios" ? Platform.APPLE_APPSTORE : Platform.GOOGLE_PLAY;
}

function precheck(productKey?: string) {
  const platform = currentIapPlatform();
  return apiFetch<IapCatalogResponse>("/api/iap/precheck", {
    method: "POST",
    headers: platform ? { "X-IAP-Platform": platform } : undefined,
    body: productKey ? { productKey } : {},
  });
}

function recurringPhase(offer: StoreOffer | null) {
  if (!offer?.pricingPhases?.length) return null;
  return (
    offer.pricingPhases.find((phase) => phase.recurrenceMode === "INFINITE_RECURRING") ??
    offer.pricingPhases[offer.pricingPhases.length - 1]
  );
}

function updateLocalizedPrice(product: StoreProduct) {
  const credit = snapshot.products.find((item) => item.productId === product.id);
  const subscriptions = snapshot.subscriptions.filter((item) => item.productId === product.id);
  const catalogItems = credit ? [credit] : subscriptions;

  // Android 구독의 월·연은 productId가 같으므로 일치한 첫 항목에서 멈추면 연간 가격이 비게 된다.
  for (const catalogItem of catalogItems) {
    const subscription = "cycle" in catalogItem;
    const offer = subscription
      ? selectOfferForCycle(product.offers, catalogItem.cycle, catalogItem.basePlanId)
      : product.offers?.[0] ?? null;
    const phase = recurringPhase(offer);
    const price = phase?.price ?? product.pricing?.price;
    const priceKey = subscription ? `${catalogItem.tier}:${catalogItem.cycle}` : catalogItem.key;
    if (price) publish({ localizedPrices: { ...snapshot.localizedPrices, [priceKey]: price } });

    const currency = phase?.currency ?? product.pricing?.currency;
    const micros = phase?.priceMicros ?? product.pricing?.priceMicros;
    const warningKey = `${product.id}:${priceKey}`;
    if (currency === "KRW" && Number.isFinite(micros)) {
      const storeKrw = Number(micros) / 1_000_000;
      if (storeKrw !== catalogItem.krw && !warnedPrices.has(warningKey)) {
        warnedPrices.add(warningKey);
        console.warn("[IAP-PRICE-MISMATCH] 서버 카탈로그와 스토어 가격이 다릅니다", {
          productId: product.id,
          catalogKrw: catalogItem.krw,
          storeKrw,
        });
      }
    }
  }
}

function transactionKey(transaction: StoreTransaction) {
  return String(
    transaction.transactionId ??
      transaction.purchaseToken ??
      transaction.nativePurchase?.purchaseToken ??
      transaction.raw?.purchaseToken ??
      "",
  );
}

function isSubscriptionProduct(productId: string) {
  return snapshot.subscriptions.some((item) => item.productId === productId);
}

async function verifyTransaction(transaction: StoreTransaction, options: { restored?: boolean } = {}) {
  const platform = currentIapPlatform();
  const productId = String(transaction.products?.[0]?.id ?? "");
  const key = transactionKey(transaction);
  if (!platform || !productId || !key || processingTransactions.has(key)) return false;
  const subscription = isSubscriptionProduct(productId);
  if (options.restored && !subscription) return false;

  const body = transactionVerificationBody(transaction, platform);
  if (!body) {
    console.error("[IAP-PENDING] 원본 거래 식별자를 찾지 못해 finish하지 않습니다", { platform, productId });
    return false;
  }

  processingTransactions.add(key);
  if (!options.restored) emit({ type: "processing", productId });
  try {
    const response = await apiFetch<Record<string, unknown>>(
      subscription ? "/api/iap/verify-subscription" : "/api/iap/verify-purchase",
      { method: "POST", body },
    );
    if (response?.ok !== true) throw new Error("결제 검증 응답이 올바르지 않습니다");

    // 서버 지급·구독 반영이 끝난 뒤에만 finish한다. 실패 시 큐에 남겨 다음 시작에 재시도한다.
    if (transaction.state === APPROVED_STATE) {
      await transaction.finish();
    }
    await queryClient.invalidateQueries({ queryKey: ["me"] });
    emit({ type: "success", productId, response });
    if (!options.restored) {
      const firstBonus = Number(response.first_bonus ?? 0);
      if (firstBonus > 0) {
        showSuccess(`첫 충전 보너스 +${firstBonus.toLocaleString("ko-KR")} 크레딧!`);
      } else {
        showSuccess(subscription ? "구독이 적용됐어요" : "크레딧 충전이 완료됐어요");
      }
    }
    return true;
  } catch (error) {
    // 401만 사용자 조치(재로그인)가 필요한 진짜 에러다. 나머지는 트랜잭션이 보존된 **보류**라
    //   화면이 "실패"로 말하면 이미 돈이 빠진 사용자가 재결제를 시도하게 된다.
    const unauthorized = error instanceof ApiError && error.status === 401;
    const message = unauthorized
      ? "로그인 후 결제를 다시 확인해 주세요"
      : "결제 확인에 실패했어요. 앱을 다시 열면 자동으로 재시도합니다";
    console.error("[IAP-PENDING] 서버 검증 실패 — finish하지 않고 보존", { productId, error });
    emit({ type: "error", productId, message, pending: !unauthorized });
    return false;
  } finally {
    processingTransactions.delete(key);
  }
}

function attachStoreListeners() {
  if (listenersAttached) return;
  listenersAttached = true;
  store.error((error: { code?: number; message?: string }) => {
    console.error("[IAP-STORE]", error);
  });
  store
    .when()
    .productUpdated((product: StoreProduct) => updateLocalizedPrice(product))
    .approved((transaction: StoreTransaction) => {
      void verifyTransaction(transaction);
    })
    .receiptUpdated(() => {
      void processPendingTransactions();
    });
}

function registerCatalog(catalog: IapCatalogResponse, platform: IapPlatform) {
  const nativePlatform = platformValue(platform);
  const definitions = [
    ...catalog.products.map((item) => ({
      id: item.productId,
      type: ProductType.CONSUMABLE,
      platform: nativePlatform,
    })),
    ...catalog.subscriptions.map((item) => ({
      id: item.productId,
      type: ProductType.PAID_SUBSCRIPTION,
      platform: nativePlatform,
      group: item.tier,
    })),
  ];
  // Android 월/연은 productId가 같으므로 같은 상품을 두 번 등록하지 않는다.
  store.register(definitions.filter((item, index, all) => all.findIndex((other) => other.id === item.id) === index));
}

/**
 * E2E 전용 — 웹 프리뷰에는 스토어가 없어 `currentIapPlatform()`이 null이고,
 * 결제 화면은 전부 "준비 중"(`enabled:false`) 경로만 그린다. 그러면 팩 목록·구독 티어 카드·
 * 월 환산 표기를 **실기 없이는 한 번도 볼 수 없다** → 레이아웃 결함이 전부 실기까지 살아 넘어간다.
 *
 * ⚠ 가격 정본이 아니다. 서버(`lib/topup.js` TOPUP_PRODUCTS · `lib/subscription.js` TIERS)가 정본이고
 *    여기는 그 값을 베낀 **화면 확인용 사본**이다. 어긋나도 실기·서버는 영향받지 않는다.
 *    VITE_E2E_MOCK 빌드가 아니면 호출되지 않는다(prod tree-shake).
 */
function e2eCatalog(): IapCatalogResponse {
  const id = (suffix: string) => `com.sotda.app.${suffix}`;
  return {
    ok: true,
    enabled: true,
    first_topup_bonus: 1100,
    products: [
      { key: "pack_1000", productId: id("credit.pack1000"), cb: 1100, krw: 1100, desc: "아이템 3회분 + 여유분", bonus: 0 },
      { key: "pack_5000", productId: id("credit.pack5000"), cb: 5500, krw: 5500, desc: "AI 토론 11회 or 10연챠 2번", bonus: 0 },
      { key: "pack_10000", productId: id("credit.pack10000"), cb: 11000, krw: 11000, desc: "10연챠 4번 or AI 토론 22회", bonus: 0 },
      { key: "pack_30000", productId: id("credit.pack30000"), cb: 33600, krw: 33000, desc: "AI 토론 67회 or 10연챠 12번", bonus: 600 },
      { key: "pack_50000", productId: id("credit.pack50000"), cb: 56500, krw: 55000, desc: "AI 토론 113회 or 10연챠 20번", bonus: 1500 },
      { key: "pack_100000", productId: id("credit.pack100000"), cb: 115000, krw: 110000, desc: "AI 토론 230회 — 최대 보너스", bonus: 5000 },
    ],
    subscriptions: [
      { tier: "coffee", cycle: "monthly", productId: id("sub.coffee.monthly"), basePlanId: null, krw: 4400, name: "가벼운 응원 (월간)" },
      { tier: "coffee", cycle: "yearly", productId: id("sub.coffee.yearly"), basePlanId: null, krw: 44000, name: "가벼운 응원 (연간)" },
      { tier: "gukbap", cycle: "monthly", productId: id("sub.gukbap.monthly"), basePlanId: null, krw: 8800, name: "든든한 응원 (월간)" },
      { tier: "gukbap", cycle: "yearly", productId: id("sub.gukbap.yearly"), basePlanId: null, krw: 88000, name: "든든한 응원 (연간)" },
    ],
  };
}

export async function initializeIap() {
  if (initializePromise) return initializePromise;
  initializePromise = (async () => {
    const platform = currentIapPlatform();
    if (E2E_MOCK) {
      // 스토어 지역화 가격도 함께 채운다 — 없으면 SubscribeScreen의 CTA가 `ready:false`로 계속 비활성이다.
      const catalog = e2eCatalog();
      const localizedPrices: Record<string, string> = {};
      for (const item of catalog.products) localizedPrices[item.key] = `₩${item.krw.toLocaleString("ko-KR")}`;
      for (const item of catalog.subscriptions) localizedPrices[`${item.tier}:${item.cycle}`] = `₩${item.krw.toLocaleString("ko-KR")}`;
      publish({ ...catalog, ready: true, loading: false, error: null, localizedPrices });
      return snapshot;
    }
    if (!platform) {
      publish({ ready: true, loading: false, enabled: false });
      return snapshot;
    }
    publish({ loading: true, error: null });
    try {
      const catalog = await precheck();
      publish({ ...catalog, loading: catalog.enabled, ready: !catalog.enabled });
      if (!catalog.enabled) return snapshot;

      attachStoreListeners();
      registerCatalog(catalog, platform);
      store.verbosity = import.meta.env.DEV ? LogLevel.WARNING : LogLevel.ERROR;
      const initializationErrors = await store.initialize([platformValue(platform)]);
      if (initializationErrors.length > 0) {
        throw new Error(initializationErrors.map((error: { message?: string }) => error.message).filter(Boolean).join(", "));
      }
      for (const product of store.products as StoreProduct[]) updateLocalizedPrice(product);
      publish({ ready: true, loading: false });
      await processPendingTransactions();
      return snapshot;
    } catch (error) {
      console.error("[IAP-INIT] 스토어 초기화 실패", error);
      publish({ ready: true, loading: false, error: "스토어 정보를 불러오지 못했어요" });
      return snapshot;
    }
  })();
  return initializePromise;
}

/**
 * 로그인 상태가 바뀐 뒤 카탈로그만 다시 받는다.
 *
 * 🔴 `initializeIap()`은 `initializePromise`로 결과를 캐시하므로 두 번째 호출은 **precheck를 다시 치지 않는다.**
 *    앱 부팅 시점은 대개 미로그인이고, 그때 받은 `first_topup_bonus`는 0이다(서버가 userId 없이는 판정하지 않는다).
 *    로그인 후에도 그 0이 그대로 굳으면 첫 충전 유저에게 "+1,100 크레딧" 안내가 영영 안 뜬다.
 *    스토어 연결·상품 등록은 이미 끝났으므로 여기서는 서버 카탈로그만 덮어쓴다.
 */
export async function refreshIapCatalog() {
  if (!currentIapPlatform()) return snapshot;
  try {
    const catalog = await precheck();
    publish({ ...catalog, ready: true, loading: false, error: null });
  } catch (error) {
    // 갱신 실패는 기존 스냅샷을 유지한다 — 이미 쓰던 카탈로그를 지우면 화면이 빈다.
    console.error("[IAP-REFRESH] 카탈로그 갱신 실패", error);
  }
  return snapshot;
}

export async function processPendingTransactions() {
  if (!snapshot.enabled) return 0;
  const pending = (store.localTransactions as StoreTransaction[]).filter((transaction) =>
    shouldProcessPending(transaction.state, APPROVED_STATE, processingTransactions.has(transactionKey(transaction))),
  );
  const results = await Promise.all(pending.map((transaction) => verifyTransaction(transaction)));
  return results.filter(Boolean).length;
}

export async function purchaseIapProduct(productKey: string) {
  const platform = currentIapPlatform();
  if (!platform) throw new Error("앱에서만 결제할 수 있어요");
  await initializeIap();
  if (!snapshot.enabled) throw new Error("결제 기능을 준비하고 있어요");

  const checked = await precheck(productKey);
  if (!checked.allowed) throw new Error("현재 이 상품을 결제할 수 없어요");
  const credit = checked.products.find((item) => item.key === productKey);
  const subscription = checked.subscriptions.find((item) => `${item.tier}:${item.cycle}` === productKey);
  const item = credit ?? subscription;
  if (!item) throw new Error("상품 정보를 찾지 못했어요");

  const product = store.get(item.productId, platformValue(platform)) as StoreProduct | undefined;
  const offer = subscription
    ? selectOfferForCycle(product?.offers, subscription.cycle, subscription.basePlanId)
    : product?.offers?.[0] ?? null;
  if (!product || !offer) throw new Error("스토어 상품 정보를 불러오는 중이에요. 잠시 후 다시 시도해 주세요");

  // 위 로컬 타입(StoreOffer)은 플러그인 Offer의 부분 구조라 대입 호환이 안 된다.
  // 넘기는 값 자체는 store.get()이 돌려준 실물 Offer이므로 여기서만 되돌려 준다
  // (로컬 타입은 raw·pricingPhases 접근 편의용이지 실제 객체를 바꾸지 않는다).
  const error = await store.order(offer as unknown as Parameters<typeof store.order>[0]);
  if (error) {
    if (error.code === ErrorCode.PAYMENT_CANCELLED) return false; // 사용자가 결제 시트를 닫은 경우는 오류 토스트를 띄우지 않는다.
    throw new Error(error.message || "결제를 시작하지 못했어요");
  }
  return true;
}

export async function restoreSubscriptions() {
  await initializeIap();
  if (!snapshot.enabled) throw new Error("결제 기능을 준비하고 있어요");
  const error = await store.restorePurchases();
  if (error) {
    // 플러그인이 주는 message는 영문 고정("Restore purchases failed")이라 그대로 띄우면 유저에게 무의미하다.
    //   원인 코드(StoreKit errorCode)는 진단에만 필요하므로 콘솔로 보내고, 화면에는 한글 안내를 던진다.
    //   2026-08-13 SE3 실기: VPN이 켜져 있으면 Apple 영수증 갱신 경로가 막혀 여기로 떨어진다
    //   (VPN을 끄자 같은 계정에서 정상 응답). 그래서 안내에 네트워크 힌트를 함께 준다.
    console.warn("[IAP] restorePurchases 실패", { code: error.code, message: error.message });
    throw new Error("구매 내역을 불러오지 못했어요. 네트워크(VPN·기내모드) 상태를 확인하고 다시 시도해 주세요");
  }
  const restored = (store.localTransactions as StoreTransaction[]).filter((transaction) =>
    isSubscriptionProduct(String(transaction.products?.[0]?.id ?? "")),
  );
  const results = await Promise.all(restored.map((transaction) => verifyTransaction(transaction, { restored: true })));
  await queryClient.invalidateQueries({ queryKey: ["me"] });
  return results.filter(Boolean).length;
}
